import type { RecoveryCandidate } from '@/lib/api/schemas/product-recovery.schema'

/**
 * How strong the evidence behind one candidate is, derived from the states of
 * the whole delivered set.
 *
 * The ordering is the measured one. Over the 743 ambiguous articles in
 * `forge_staging_test` on 2026-09-12, 207 hold exactly one piece recorded sold,
 * 401 hold several and 135 hold none. So a sole sold piece and one of four sold
 * pieces are not the same kind of answer, and a flat list would hide that.
 *
 * State ranks and explains a candidate. It never decides one: marketplace sales
 * were never written into 1C, so a piece recorded sold there does not thereby
 * own a particular order, and a piece with no recorded sale is not thereby
 * unsold. A decision still needs a person or an exact identifier relation.
 */
export type CandidateStrength =
  | 'sole_sold_piece'
  | 'one_of_several_sold'
  | 'not_sold'
  | 'no_sold_piece_in_article'
  | 'state_not_recorded'

const STRENGTH_RANK: Record<CandidateStrength, number> = {
  sole_sold_piece: 0,
  one_of_several_sold: 1,
  not_sold: 2,
  no_sold_piece_in_article: 3,
  state_not_recorded: 4,
}

export type RankedCandidate = {
  candidate: RecoveryCandidate
  strength: CandidateStrength
  /**
   * Other candidates in this set carrying the same recorded weight and size.
   * 58 of the 743 ambiguous articles hold pieces identical on every fact we
   * record, and an operator must be told that rather than left comparing two
   * rows that read alike by accident.
   */
  indistinguishableFrom: readonly string[]
}

function strengthOf(
  candidate: RecoveryCandidate,
  soldCount: number,
): CandidateStrength {
  if (candidate.currentState === null) return 'state_not_recorded'
  if (candidate.currentState === 'sold') {
    return soldCount === 1 ? 'sole_sold_piece' : 'one_of_several_sold'
  }
  return soldCount === 0 ? 'no_sold_piece_in_article' : 'not_sold'
}

/**
 * The recorded facts that separate two pieces of one article. Weight separates
 * the pieces in 584 of 743 ambiguous articles and size adds 30 more; nothing
 * else we record separates any further pair.
 */
function distinctionKey(candidate: RecoveryCandidate): string {
  return JSON.stringify([candidate.weightGrams, candidate.size])
}

/**
 * Rank a candidate set strongest first, leaving equal strengths in the order the
 * server sent them.
 */
export function rankCandidates(
  candidates: readonly RecoveryCandidate[],
): RankedCandidate[] {
  const soldCount = candidates.filter((each) => each.currentState === 'sold').length
  const byKey = new Map<string, string[]>()
  for (const candidate of candidates) {
    const key = distinctionKey(candidate)
    byKey.set(key, [...(byKey.get(key) ?? []), candidate.productId])
  }
  return candidates
    .map((candidate) => ({
      candidate,
      strength: strengthOf(candidate, soldCount),
      indistinguishableFrom: (byKey.get(distinctionKey(candidate)) ?? [])
        .filter((productId) => productId !== candidate.productId),
    }))
    .sort((left, right) => STRENGTH_RANK[left.strength] - STRENGTH_RANK[right.strength])
}

/** True when no recorded fact separates every candidate from every other one. */
export function noRecordedFactSeparates(ranked: readonly RankedCandidate[]): boolean {
  return ranked.length > 1 && ranked.every((each) => each.indistinguishableFrom.length > 0)
}
